import { useMemo } from "react";

import { useLocalization } from "@fluent/react";

import { useBaiTransactionTypes } from "../../../reports/hooks/useBaiTransactionTypes";
import { LIVE_LEDGER_BAI_CODES_QUERY_KEY } from "../../constants";
import { useMultiSelectFilter } from "../../hooks/useMultiSelectFilter";

import MultiSelectFilterPanel from "./MultiSelectFilterPanel";

import type { BaiTransactionType } from "../../../reports/hooks/useBaiTransactionTypes";

export interface BaiCodesFilterPanelProps {
  close: () => void;
  onApply: () => void;
}

const DIRECTION_LABELS: Record<BaiTransactionType["direction"], string> = {
  credit: "live-ledger-filter-bai-code-group-credit",
  debit: "live-ledger-filter-bai-code-group-debit",
  misc: "live-ledger-filter-bai-code-group-other",
};

const DIRECTION_ORDER: Record<BaiTransactionType["direction"], number> = {
  credit: 0,
  debit: 1,
  misc: 2,
};

const formatTypeLabel = (type: BaiTransactionType): string =>
  type.description ? `${type.code} — ${type.description}` : type.code;

const getEmptyLabel = (
  l10n: { getString: (id: string) => string },
  isLoading: boolean,
  error: string | null
): string => {
  if (isLoading) return l10n.getString("loading");
  if (error) return error;
  return l10n.getString("live-ledger-filter-bai-code-empty");
};

const BaiCodesFilterPanel = ({ close, onApply }: BaiCodesFilterPanelProps) => {
  const { l10n } = useLocalization();
  const { types, isLoading, error } = useBaiTransactionTypes();

  const options = useMemo(
    () =>
      [...types]
        .sort(
          (a, b) => DIRECTION_ORDER[a.direction] - DIRECTION_ORDER[b.direction]
        )
        .map((type) => ({
          value: type.code,
          label: formatTypeLabel(type),
          group: l10n.getString(DIRECTION_LABELS[type.direction]),
        })),
    [types, l10n]
  );

  const validCodes = useMemo(
    () => (isLoading ? undefined : types.map((type) => type.code)),
    [types, isLoading]
  );

  const { selectedValues, setSelectedValues } = useMultiSelectFilter({
    queryKey: LIVE_LEDGER_BAI_CODES_QUERY_KEY,
    validValues: validCodes,
  });

  return (
    <MultiSelectFilterPanel
      options={options}
      selectedValues={selectedValues}
      setSelectedValues={setSelectedValues}
      close={close}
      onApply={onApply}
      searchLabel={l10n.getString("live-ledger-filter-bai-code-search")}
      emptyLabel={getEmptyLabel(l10n, isLoading, error)}
      applyLabel={l10n.getString("live-ledger-filter-bai-code-apply")}
      removeLabel={l10n.getString("live-ledger-filter-bai-code-remove")}
      testIdPrefix="ledger-bai-codes-filter"
      getSearchText={(option) => option.label}
    />
  );
};

export default BaiCodesFilterPanel;
